<template>
  <div class="heatmap-report"
       v-if="applyData">
    <heatmap :mode="'only'"
             :aid="aid"
             :infoData="infoData"
             :activeSelector="activeSelector"
             :userActionData="userActionData"
             :conversion="initialConversion"
             :activityStatistics="initialActivityStatistics"
             :initialReferer="initialReferer"
             :initialDevice="initialDevice"
             :initialEventSource="initialEventSource"
             :initialEventType="initialEventType"
             :initialHeatmapType="initialHeatmapType"
             :initialCampaign="initialCampaign"
             :initialUtid="initialUtid"
             @onHeatmapLoadEnd="onHeatmapLoadEnd"
             @onHeatmapData="onHeatmapData"
             @onInfoData="onInfoData" />
  </div>
</template>

<script>
import store from '@/store'
import { parse, format, addDays, isBefore } from 'date-fns'
import { mapMutations, mapState } from 'vuex'
import { parseJwt } from '@/libs/utils'
import { getAuthToken } from '@/libs/auth-helper'
import { DOCUMENT_TITLE_PREFIX } from '@/libs/constants'
import { getApply } from '@/api/apply'
import { getReportPeriod } from '@/api/report'
import { getDialog } from '@/libs/vue-utils'
import { changeLocale } from '@/libs/i18n'

import Heatmap from '@/components/heatmap'

const openDialog = getDialog('dialogType')

function loadApplyData (aid, query) {
  return Promise.all([
    getApply(aid),
    getReportPeriod(aid)
  ]).then(result => {
    const apply = result[0]
    const period = result[1]

    if (!apply || !apply.meta.canViewReport) {
      return null
    }

    const tokenData = parseJwt(getAuthToken().accessToken)
    const userId = tokenData.identity.user_id
    const userNum = tokenData.identity.user_num

    if (userId !== 'admin' && userId !== 'demo' && !apply.meta.shared) {
      if (userId !== apply.userId || userNum !== apply.userNum) {
        return null
      }
    }

    const dateFormat = 'YYYY-MM-DD'
    const minDate = parse(period.minDate)
    const maxDate = parse(period.maxDate)
    let startDate = addDays(maxDate, -6)
    let endDate

    if (isBefore(startDate, minDate)) {
      startDate = parse(minDate)
    }

    if (query.startDate) {
      startDate = parse(query.startDate)
    }

    if (query.endDate) {
      endDate = parse(query.endDate)
    }

    store.commit('setApplyData', apply)
    store.commit('setReportPeriod', {
      start: format(startDate, dateFormat),
      end: format(endDate || maxDate, dateFormat),
      min: format(minDate, dateFormat),
      max: format(maxDate, dateFormat)
    })
    document.title = `${DOCUMENT_TITLE_PREFIX} - Report(${apply.pageName})`
    return apply
  })
}

export default {
  name: 'HeatmapReport',
  components: {
    Heatmap
  },
  props: [
    'cacheManager',
    'isAMP'
  ],
  data () {
    return {
      reportData: null,
      reportName: this.$route.query.eventType || 'click',
      metric: null,
      activeSelector: null,
      userActionData: null,
      infoData: null
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    aid () {
      return this.$route.params.aid
    },
    initialDevice () {
      return this.$route.query.device || 'desktop'
    },
    initialReferer () {
      return this.$route.query.referer || 'all'
    },
    initialEventSource () {
      return this.$route.query.eventSource || 'all'
    },
    initialEventType () {
      return this.$route.query.eventType || 'click'
    },
    initialHeatmapType () {
      return Number(this.$route.query.heatmapType) || 0
    },
    initialConversion () {
      return this.reportName !== 'stream' ? null : this.nullOrBool(this.$route.query.conversion || 'null')
    },
    initialActivityStatistics () {
      return this.reportName !== 'stream' ? 'median' : this.$route.query.activityStatistics || 'median'
    },
    initialCampaign () {
      return this.$route.query.campaign && this.$route.query.campaign !== 'null' ? JSON.parse(this.$route.query.campaign) : null
    },
    initialUtid () {
      return this.$route.query.utid && this.$route.query.utid !== 'null' ? this.$route.query.utid : null
    },
    ctaData () {
      if (this.$route.query.ctaData) {
        const data = JSON.parse(this.$route.query.ctaData)
        if (!data || !data.coords) {
          return null
        }
        if (!data.except) {
          data.except = false
        }
        return data
      }
      return null
    },
    reportComponent () {
      switch (this.reportName) {
        case 'click':
          return 'ReportClick'
        case 'tap':
          return 'ReportTap'
        case 'zoom':
          return 'ReportZoom'
        case 'move':
          return 'ReportMove'
        case 'swipe':
          return 'ReportSwipe'
        case 'scroll':
          return 'ReportScroll'
        case 'stream':
          return 'ReportStream'
      }

      if (this.isAMP) {
        switch (this.reportName) {
          case 'click':
          case 'tap':
            return 'ReportClick'
          case 'zoom':
          case 'move':
            return 'ReportMove'
          case 'scroll':
            return 'ReportScroll'
          case 'stream':
            return 'ReportStream'
        }
      }
      return null
    }
  },
  created () {
    if (this.ctaData) {
      this.setCtaData(this.ctaData)
    }

    const lang = this.$route.query.lang || 'ko'
    if (this.$i18n !== lang) {
      changeLocale(this.$i18n, lang)
    }
  },
  beforeRouteEnter (to, from, next) {
    const aid = to.params.aid

    if (!aid) {
      openDialog('report.invalidRequest').then(() => {
        return next({ name: 'dashboard' })
      })
    } else {
      const startDate = to.query.startDate || new Date()
      const endDate = to.query.endDate || addDays(startDate, -7)
      store.commit('setReportPeriod', {
        start: format(startDate, 'YYYY-MM-DD'),
        end: format(endDate, 'YYYY-MM-DD')
      })

      loadApplyData(aid, to.query).then(result => {
        if (!result) {
          openDialog('report.invalidRequest').then(() => {
            return next({ name: 'dashboard' })
          })
        } else {
          return next()
        }
      }).catch(err => {
        console.error(err)
        return next({ name: 'dashboard' })
      })
    }
  },
  methods: {
    ...mapMutations([
      'setCtaData'
    ]),
    onHeatmapData (key, value) {
      this.reportName = key
      this.reportData = value
    },
    onInfoData (value) {
      this.infoData = value
    },
    nullOrBool (value) {
      if (value === 'null') {
        return null
      } else if (value === 'true') {
        return true
      } else if (value === 'false') {
        return false
      } else {
        return Boolean(Number(value))
      }
    },
    onHeatmapLoadEnd () {
      setTimeout(() => {
        window.status = 'ready'
      }, 200)
    }
  }
}
</script>

<style lang="scss" scoped>
.heatmap-report {
  & /deep/ .coords_rect_except_info {
    display: none;
  }
}
</style>
